import { useMemo } from 'react';

import { api } from '@/api';
import { useStudyGuard } from '@/shared/guard';
import {
  hasStudyResultScanType,
  mapPriorStudiesToStudyResults,
  mapPriorStudyToStudy,
  mapRadiologistScanToCurrentStudy,
} from '@/shared/util';

export function usePriorStudies() {
  const { radiologistScan, studyId } = useStudyGuard();

  const { data, isError, isLoading } = api.fct.comparison.query.useGetPriorStudies(
    {
      payload: {
        params: {
          currentStudyId: studyId,
          patientId: radiologistScan.patientID,
        },
      },
    },
    { enabled: false },
  );

  const priorStudies = useMemo(() => data?.priorStudies ?? [], [data?.priorStudies]);

  const previousStudyResults = useMemo(
    () => mapPriorStudiesToStudyResults(priorStudies),
    [priorStudies],
  );

  const currentStudy = useMemo(
    () => mapRadiologistScanToCurrentStudy(studyId, radiologistScan),
    [radiologistScan, studyId],
  );

  const mostRecentPreviousStudy = useMemo(
    () => (priorStudies[0] ? mapPriorStudyToStudy(priorStudies[0]) : undefined),
    [priorStudies],
  );

  const hasPreviousStudyResults = useMemo(
    () => previousStudyResults.some(hasStudyResultScanType),
    [previousStudyResults],
  );

  const value = useMemo(
    () => ({
      currentStudy,
      hasPreviousStudyResults,
      isError,
      isLoading,
      mostRecentPreviousStudy,
      previousStudyResults,
      priorStudies,
      priorStudyCount: priorStudies.length,
    }),
    [
      currentStudy,
      hasPreviousStudyResults,
      isError,
      isLoading,
      mostRecentPreviousStudy,
      priorStudies,
      previousStudyResults,
    ],
  );

  return value;
}
